import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// No hardcoded fallback — REACT_APP_API_URL is always set by the deploy
// workflows. See identical comment in OnboardingPage.jsx for context.
const API_BASE = process.env.REACT_APP_API_URL;

/**
 * Landing for the email-confirmation link sent on /tenants/register. Hits
 * GET /tenants/verify-email?token=X and surfaces the outcome. Unauthenticated
 * — the token itself is the credential.
 */
export const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState(token ? "checking" : "missing");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/tenants/verify-email?token=${encodeURIComponent(token)}`);
        if (!res.ok) {
          if (!cancelled) setState("error");
          return;
        }
        const data = await res.json();
        if (!cancelled) setState(data?.verified ? "ok" : "invalid");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  let icon, title, body, cta;
  if (state === "checking") {
    return (
      <Stack sx={{ p: 6, alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Confirming your email…
        </Typography>
      </Stack>
    );
  }
  if (state === "ok") {
    icon = <CheckCircleIcon sx={{ fontSize: 64, color: "#16a34a" }} />;
    title = "Email confirmed.";
    body = "Your account is fully active. You can sign in now.";
    cta = { label: "Sign in", path: "/login" };
  } else if (state === "missing") {
    icon = <ErrorOutlineIcon sx={{ fontSize: 64, color: "#dc2626" }} />;
    title = "No token in URL.";
    body = "Open the confirmation email and click the link, or request a new one from your account settings.";
    cta = { label: "Sign in", path: "/login" };
  } else {
    icon = <ErrorOutlineIcon sx={{ fontSize: 64, color: "#dc2626" }} />;
    title = "Confirmation link is invalid or expired.";
    body = "Sign in and request a new verification email from your account settings.";
    cta = { label: "Sign in", path: "/login" };
  }

  return (
    <Stack sx={{ p: { xs: 3, md: 6 }, alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Card variant="outlined" sx={{ borderRadius: 3, maxWidth: 520 }}>
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          {icon}
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>{title}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{body}</Typography>
          <Box>
            <Button variant="contained" sx={{ backgroundColor: "#1F4E78" }} onClick={() => navigate(cta.path)}>
              {cta.label}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default VerifyEmailPage;
